/** Small shared building blocks for Dashboard + Revenue. */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../../theme/theme';
import { PERIODS, STATUS_META } from './dashboardConfig';
import { DashboardPeriod, TokenCounts } from '../../hooks/useQueueDashboard';

/** Segmented period control (Today / Weekly / Monthly / Yearly / Custom). */
export const PeriodTabs: React.FC<{ value: DashboardPeriod; onChange: (p: DashboardPeriod) => void }> = ({ value, onChange }) => (
  <Box sx={{ display: 'inline-flex', bgcolor: colors.gray100, p: 0.5, borderRadius: '12px', gap: 0.5 }}>
    {PERIODS.map((p) => {
      const active = p.key === value;
      return (
        <Box
          key={p.key}
          component="button"
          onClick={() => onChange(p.key)}
          sx={{
            border: 'none',
            bgcolor: active ? colors.backgroundPaper : 'transparent',
            color: active ? colors.primary : colors.textSecondary,
            px: 2,
            py: 1,
            borderRadius: '9px',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
            boxShadow: active ? '0 1px 2px rgba(15,23,42,.06)' : 'none',
            fontFamily: 'inherit',
          }}
        >
          {p.label}
        </Box>
      );
    })}
  </Box>
);

/** Colored status-count chips (only statuses with count > 0). */
export const StatusChips: React.FC<{ counts: TokenCounts }> = ({ counts }) => {
  const items = STATUS_META.filter((s) => counts[s.key] > 0);
  if (!items.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
      {items.map((s) => (
        <Box
          key={s.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            bgcolor: colors.gray50,
            border: `1px solid ${colors.border}`,
            px: 1.25,
            py: 0.75,
            borderRadius: '8px',
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{counts[s.key]}</Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: 12.5 }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
};
